import React from 'react'
import { Box } from '@the-platform-group/component-library'
import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { TruncatedText } from 'components/TruncatedText'
import FilesCell from './FilesCell'
import { bulkDownloadCustomerUploadInstructionUrl } from 'services/customerUploadInstructions'
import useCustomerRequestInstructions from '../../../IrwinAssistant/CustomerPreferences/hooks/useCustomerRequestInstructions'
import useCustomerAccountManager from '../../../IrwinAssistant/CustomerPreferences/hooks/useCustomerAccountManager'

const CustomerDetailPanel = ({ customer }) => {
  const {
    customerInstructions,
    isFetchingCustomerInstructions,
    handleFileDownload,
  } = useCustomerRequestInstructions(customer)

  const { queryParams: accountManagerParams } = useCustomerAccountManager({
    customer,
  })

  const { managers, receivesAssistantNotifications } = accountManagerParams

  // Format account managers for display
  const accountManagersDisplay =
    managers?.length > 0 ? managers.map(manager => manager.label).join(', ') : null
  const includeInEmailDisplay = receivesAssistantNotifications ? 'Yes' : 'No'
  // Add account manager info to each instruction row, or create a single row if no instructions but there are managers
  let displayRows = []
  if (customerInstructions?.length > 0) {
    displayRows = customerInstructions.map(instruction => ({
      ...instruction,
      accountManagers: accountManagersDisplay,
      includeInEmail: includeInEmailDisplay,
    }))
  } else if (managers?.length > 0) {
    displayRows = [
      {
        id: 'account-managers-only',
        accountManagers: accountManagersDisplay,
        includeInEmail: includeInEmailDisplay,
      },
    ]
  }

  const columns = [
    {
      field: 'accountManagers',
      flex: null,
      minWidth: 450,
      renderCell: ({ value }) => (
        <Box width="100%">
          <TruncatedText content={value} contentPreview={value} maxLines={3} />
        </Box>
      ),
    },
    {
      field: 'includeInEmail',
      headerName: 'Include In Email?',
      flex: null,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'instructions',
      headerName: 'Irwin Assistant Instructions',
      flex: null,
      minWidth: 450,
      renderCell: ({ value }) => (
        <Box width="100%">
          <TruncatedText content={value || ''} contentPreview={value || ''} maxLines={3} />
        </Box>
      ),
    },
    {
      field: 'attachments',
      flex: null,
      minWidth: 120,
      renderCell: ({ row }) => {
        if (!row.filePaths || row.filePaths?.length <= 0) {
          return '-'
        }

        const value = { ...row, name: row.customer?.name }
        return (
          <FilesCell
            value={value || {}}
            handleFileDownload={handleFileDownload}
            getBulkDownloadUrl={bulkDownloadCustomerUploadInstructionUrl}
          />
        )
      },
    },
  ]

  return (
    <Box sx={{ p: 2, backgroundColor: theme => theme.palette.primary.bright }}>
      <DataGrid
        disableColumnFilter
        disableColumnReorder
        disableExport
        disableDensitySelector
        disableColumnMenu
        disableColumnSelector
        disableMultipleColumnsSorting
        disableReset
        pagination={false}
        autoHeight
        hideFooter
        rowHeight={100}
        rows={displayRows}
        columns={columns}
        loading={isFetchingCustomerInstructions}
        localeText={{
          noRowsLabel: 'No customer information available.',
        }}
        sx={{
          border: 'none',
          backgroundColor: theme => theme.palette.primary.bright,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: theme => theme.palette.primary.bright,
          },
          '& .MuiDataGrid-cell': {
            backgroundColor: theme => theme.palette.primary.bright,
          },
          '& .MuiDataGrid-row': {
            backgroundColor: theme => theme.palette.primary.bright,
          },
        }}
      />
    </Box>
  )
}

export default CustomerDetailPanel
